<script setup>
// Fangate Live-Sales analytics — the "Transactions" + "Buyers" vault module.
// Everything is served from our own FangateSale table (FangateAPI.getSales →
// Integrations::FangateSalesController), bounded by the selected period; the
// status / sort / search / buyer-aggregation all happen client-side here, so
// no live Fangate calls. Product names come from the vault's product list
// (cached), falling back to "#<id>". Gated on a connected Live-Sales webhook.
import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue';
import FangateAPI from 'dashboard/api/fangate';
import FangateAssignChatModal from './FangateAssignChatModal.vue';

const props = defineProps({
  // The chat-account = inbox the FangateAPI is scoped to (account.id IS the inbox id).
  inboxId: { type: [Number, String], default: null },
  isMobile: { type: Boolean, default: false },
});

const DAY = 86400000;

const loading = ref(true);
const webhookOk = ref(true);
const sales = ref([]);              // current Transactions page (server-paginated)
const nameMap = ref({});
const contentMap = ref({});         // product_id -> content file URL (same as the panel's Content chip)
// Server-computed period tiles + pagination — the heavy GROUP BY now runs in
// Postgres (indexed), so the client only holds one 50-row page at a time.
const summary = ref({ earnings: 0, successful: 0, pending: 0, failed: 0, currency: 'EUR' });
const meta = ref({ page: 1, per_page: 50, total: 0, total_pages: 1, has_more: false });
const page = ref(1);
const buyerRows = ref([]);          // current Buyers page
const buyerTotalsRaw = ref({ count: 0, earn: 0, currency: 'EUR' });
const buyerTxns = reactive({});     // email -> [txns], lazy-loaded on row expand

const view = ref('tx');           // 'tx' | 'buy'
const periods = reactive({ tx: 'thismonth', buy: 'alltime' }); // per-view period defaults — Transactions = This month, Buyers = All time
const customRanges = reactive({ tx: null, buy: null });
const period = computed(() => periods[view.value]);
const status = ref('successful'); // all | successful | pending | failed
const sort = ref('new');          // new | price_high | price_low
const search = ref('');
const menuOpen = ref(false);
const calOpen = ref(false);
const sortOpen = ref(false);
const expanded = reactive({});    // buyer email -> bool

const calView = ref(startOfMonth(new Date()));
const selStart = ref(null);
const selEnd = ref(null);

// ---- helpers ---------------------------------------------------------------
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function startOfToday() { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }
function startOfWeek(d) { const x = new Date(d); const dow = (x.getDay() + 6) % 7; x.setDate(x.getDate() - dow); x.setHours(0, 0, 0, 0); return x; }
const fmtCur = (n, cur) => new Intl.NumberFormat('en-IE', { style: 'currency', currency: cur || 'EUR' }).format(Number(n || 0));
const fmtDT = d => d ? d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) + ', ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';
const fmtD = d => d ? d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '';
function priceEst(t) {
  if (t.set_price != null && t.set_price !== '') return Number(t.set_price); // exact when Fangate sent it
  return t.status === 'successful' ? Math.round((Number(t.seller_earning || 0) + 0.5) / 0.875) : 0; // est. fallback
}
function pname(t) { return t.product_name || nameMap.value[String(t.product_id)] || ('#' + (t.product_id || '?')); }
function curOf(t) { return t.currency || 'EUR'; }

const PERIODS = [['today', 'Today'], ['yesterday', 'Yesterday'], ['thisweek', 'This week'], ['lastweek', 'Last week'], ['thismonth', 'This month'], ['alltime', 'All time'], ['custom', 'Custom…']];
const SORTS = [['new', 'Newest'], ['price_high', 'Price (highest)'], ['price_low', 'Price (lowest)']];

function rangeDates() {
  const today = startOfToday();
  const end = new Date(today.getTime() + DAY - 1);
  const cr = customRanges[view.value];
  switch (period.value) {
    case 'yesterday': return { from: new Date(today.getTime() - DAY), to: new Date(today.getTime() - 1) };
    case 'thisweek': return { from: startOfWeek(today), to: end };
    case 'lastweek': return { from: new Date(startOfWeek(today).getTime() - 7 * DAY), to: new Date(startOfWeek(today).getTime() - 1) };
    case 'thismonth': return { from: startOfMonth(today), to: end };
    case 'alltime': return { from: null, to: end };
    case 'custom': return cr ? { from: cr[0], to: cr[1] } : { from: today, to: end };
    default: return { from: today, to: end };
  }
}
const periodLabel = computed(() => {
  const cr = customRanges[view.value];
  if (period.value === 'custom' && cr) return fmtD(cr[0]) + ' – ' + fmtD(cr[1]);
  return (PERIODS.find(p => p[0] === period.value) || PERIODS[0])[1];
});
const sortLabel = computed(() => (SORTS.find(s => s[0] === sort.value) || SORTS[0])[1]);

// ---- data ------------------------------------------------------------------
// All filtering / aggregation / pagination is server-side now (indexed GROUP BY +
// LIMIT/OFFSET — see Integrations::FangateSalesController). The client just renders
// the returned page + the period summary.
function commonParams(resetPage) {
  if (resetPage) page.value = 1;
  const r = rangeDates();
  return {
    from: r.from ? r.from.toISOString() : null,
    to: r.to ? r.to.toISOString() : null,
    search: (search.value || '').trim() || undefined,
    page: page.value,
    perPage: 50,
  };
}

// Persist the selected period per view (survives reload / navigating away).
const LS_PERIOD = 'fangate_tx_period';
function savePeriod() {
  try {
    const cr = {};
    ['tx', 'buy'].forEach(k => { const r = customRanges[k]; cr[k] = r ? [r[0].toISOString(), r[1].toISOString()] : null; });
    localStorage.setItem(LS_PERIOD, JSON.stringify({ periods: { tx: periods.tx, buy: periods.buy }, customRanges: cr }));
  } catch (_) { /* ignore quota / privacy mode */ }
}
function restorePeriod() {
  try {
    const s = JSON.parse(localStorage.getItem(LS_PERIOD) || 'null');
    if (!s) return;
    ['tx', 'buy'].forEach(k => { if (s.periods && s.periods[k]) periods[k] = s.periods[k]; });
    if (s.customRanges) ['tx', 'buy'].forEach(k => { const r = s.customRanges[k]; if (Array.isArray(r)) customRanges[k] = [new Date(r[0]), new Date(r[1])]; });
  } catch (_) { /* ignore */ }
}

// Short-lived response cache — re-opening the same view/period/page doesn't re-hit
// the server. Sales are live → 45s TTL; cleared on inbox change.
const CACHE_TTL = 45000;
const respCache = new Map();
async function getSalesCached(opts, key) {
  const hit = respCache.get(key);
  if (hit && (Date.now() - hit.ts) < CACHE_TTL) return hit.data;
  const d = (await FangateAPI.getSales(props.inboxId, opts)) || {};
  respCache.set(key, { ts: Date.now(), data: d });
  return d;
}
const mapRow = s => ({ ...s, _date: s.occurred_at ? new Date(s.occurred_at) : null });
async function fetchData(resetPage = true) {
  if (!props.inboxId) { sales.value = []; buyerRows.value = []; loading.value = false; return; }
  savePeriod();
  loading.value = true;
  const p = commonParams(resetPage);
  if (view.value === 'tx') {
    const key = JSON.stringify([props.inboxId, 'tx', p.from, p.to, p.search || '', p.page, status.value, sort.value]);
    const d = await getSalesCached({ ...p, view: 'tx', status: status.value, sort: sort.value }, key);
    sales.value = (d.sales || []).map(mapRow);
    summary.value = d.summary || summary.value;
    meta.value = d.meta || meta.value;
  } else {
    const key = JSON.stringify([props.inboxId, 'buyers', p.from, p.to, p.search || '', p.page]);
    const d = await getSalesCached({ ...p, view: 'buyers' }, key);
    buyerRows.value = d.buyers || [];
    summary.value = d.summary || summary.value;
    buyerTotalsRaw.value = d.totals || buyerTotalsRaw.value;
    meta.value = d.meta || meta.value;
  }
  loading.value = false;
}
// Mirrors FangatePanel.openContentFor — the content file URL Fangate exposes on the
// product's first media (source → preview_url → url), product preview/thumb last.
function productContentUrl(p) {
  const media = Array.isArray(p && p.media) ? p.media : [];
  const first = media[0] || {};
  return first.source || first.preview_url || first.url || (p && (p.preview || p.thumb)) || '';
}
async function loadNames() {
  try {
    const res = await FangateAPI.listProducts({ page: 1, limit: 100, inboxId: props.inboxId });
    // listProducts already unwraps the outer `{ data: … }`, so `res` is
    // `{ pages_total, collection_link, data: [...products] }` — the products are
    // in res.data (this was the bug: reading res.products/res.items left the
    // contentMap EMPTY, so the Content link always fell back to getProduct).
    const list = res?.data ?? res?.products ?? res?.items ?? res ?? [];
    const m = {}; const cm = {};
    (Array.isArray(list) ? list : []).forEach(p => {
      if (p && p.id != null) { m[String(p.id)] = p.title || p.name || ''; cm[String(p.id)] = productContentUrl(p); }
    });
    nameMap.value = m;
    contentMap.value = cm;
  } catch (_) { /* names are a bonus — fall back to #id */ }
}
async function init() {
  if (!props.inboxId) { loading.value = false; return; }
  try { await FangateAPI.hydrateInbox(props.inboxId); } catch (_) { /* trust cache */ }
  webhookOk.value = FangateAPI.getWebhookState(props.inboxId) !== 'not_connected';
  restorePeriod();
  loadNames();
  await fetchData(true);
}

// ---- pagination ------------------------------------------------------------
const totalPages = computed(() => Math.max(1, meta.value.total_pages || 1));
function goPage(p) {
  if (p < 1 || p > totalPages.value || p === (meta.value.page || 1)) return;
  page.value = p;
  fetchData(false);
}

// ---- derived (server already filtered / sorted / paginated) ----------------
function normSt(t) {
  if (t.status) return t.status;
  const e = t.event || '';
  if (e.indexOf('pending') >= 0) return 'pending';
  if (e.indexOf('fail') >= 0) return 'failed';
  return 'successful';
}
const tiles = computed(() => ({
  earnings: Number(summary.value.earnings || 0),
  cur: summary.value.currency || 'EUR',
  successful: summary.value.successful || 0,
  pending: summary.value.pending || 0,
  failed: summary.value.failed || 0,
}));
const txList = computed(() => sales.value);
const buyers = computed(() => buyerRows.value.map(b => ({ ...b, txns: buyerTxns[b.email] || [] })));
const buyerTotals = computed(() => ({
  count: buyerTotalsRaw.value.count || 0,
  earn: Number(buyerTotalsRaw.value.earn || 0),
  cur: buyerTotalsRaw.value.currency || 'EUR',
}));

// Content deeplink — opens the SAME target as the panel's "Content" chip
// (product.media[0].source …). Resolved ON DEMAND per click so it works for ANY
// product, not just the first 100 preloaded into contentMap; the preload + a
// per-session cache keep known/repeat clicks instant.
async function openContent(t) {
  const pid = String(t.product_id || '');
  const known = contentMap.value[pid];
  if (known) { window.open(known, '_blank', 'noopener,noreferrer'); return; }
  // Open the tab synchronously (inside the click gesture) so it isn't popup-blocked,
  // then resolve the content URL on demand and navigate the tab to it.
  const w = window.open('', '_blank');
  let url = 'https://app.fangate.info';
  try {
    const p = await FangateAPI.getProduct(pid, { inboxId: props.inboxId });
    const resolved = productContentUrl(p || {});
    if (resolved) { url = resolved; contentMap.value[pid] = resolved; }
  } catch (_) { /* keep the Fangate-app fallback */ }
  if (w) { try { w.opener = null; w.location.href = url; } catch (_) { /* navigation blocked */ } }
  else { window.open(url, '_blank', 'noopener,noreferrer'); }
}

// ---- calendar --------------------------------------------------------------
const calCells = computed(() => {
  const y = calView.value.getFullYear(), m = calView.value.getMonth();
  const startDay = (new Date(y, m, 1).getDay() + 6) % 7;
  const days = new Date(y, m + 1, 0).getDate();
  const today = startOfToday();
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push({ blank: true, key: 'b' + i });
  for (let d = 1; d <= days; d++) {
    const date = new Date(y, m, d);
    const inRange = selStart.value && selEnd.value && date >= selStart.value && date <= selEnd.value;
    const edge = (selStart.value && +date === +selStart.value) || (selEnd.value && +date === +selEnd.value);
    cells.push({ d, date, key: 'd' + d, muted: date > today, rng: !!inRange, edge: !!edge });
  }
  return cells;
});
const calMonth = computed(() => calView.value.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }));
const calHint = computed(() => selStart.value ? (fmtD(selStart.value) + (selEnd.value ? ' – ' + fmtD(selEnd.value) : ' – …')) : 'Pick start date');
function pickDay(c) {
  if (c.blank || c.muted) return;
  if (!selStart.value || (selStart.value && selEnd.value)) { selStart.value = c.date; selEnd.value = null; }
  else if (c.date < selStart.value) { selStart.value = c.date; }
  else { selEnd.value = c.date; }
}
function applyCustom() {
  if (!selStart.value || !selEnd.value) return;
  customRanges[view.value] = [selStart.value, new Date(selEnd.value.getTime() + DAY - 1)];
  periods[view.value] = 'custom';
  closePops();
  fetchData(true);
}

// ---- ui --------------------------------------------------------------------
// ⋮ "Assign to Chat" on a tx row → opens the shared assign-email-to-chat modal.
const assignRef = ref(null);
const assignMenu = reactive({ open: false, x: 0, y: 0, email: '' });
function openAssignMenu(e, t) {
  const r = e.currentTarget.getBoundingClientRect();
  assignMenu.x = Math.min(r.left - 130, window.innerWidth - 170);
  assignMenu.y = r.bottom + 4;
  assignMenu.email = t.buyer_email || '';
  assignMenu.open = true;
}
function openAssignModal() {
  const email = assignMenu.email;
  assignMenu.open = false;
  if (email && assignRef.value) assignRef.value.open(email);
}
function closePops() { menuOpen.value = false; calOpen.value = false; sortOpen.value = false; assignMenu.open = false; }
function togglePeriod() { const o = menuOpen.value || calOpen.value; closePops(); if (!o) menuOpen.value = true; }
function selectPeriod(p) {
  if (p === 'custom') { menuOpen.value = false; calOpen.value = true; return; }
  periods[view.value] = p; customRanges[view.value] = null; closePops(); fetchData(true);
}
function toggleSort() { const o = sortOpen.value; closePops(); sortOpen.value = !o; }
function selectSort(s) { sort.value = s; closePops(); fetchData(true); }
async function toggleExpand(email) {
  expanded[email] = !expanded[email];
  if (expanded[email] && !buyerTxns[email] && props.inboxId) {
    const r = rangeDates();
    const d = (await FangateAPI.getSales(props.inboxId, {
      from: r.from ? r.from.toISOString() : null, to: r.to ? r.to.toISOString() : null,
      view: 'tx', status: 'successful', search: email, page: 1, perPage: 100,
    })) || {};
    buyerTxns[email] = (d.sales || []).map(mapRow);
  }
}
function switchView(v) { view.value = v; closePops(); fetchData(true); }

// Status change → reload page 1; sort handled in selectSort; search debounced.
watch(status, () => fetchData(true));
let searchTimer = null;
watch(search, () => { clearTimeout(searchTimer); searchTimer = setTimeout(() => fetchData(true), 350); });

// Dismissible scope note — remembers the dismissal per browser.
const SCOPE_NOTE_KEY = 'fangate_tx_scope_note_dismissed';
const scopeNoteDismissed = ref(false);
try { scopeNoteDismissed.value = localStorage.getItem(SCOPE_NOTE_KEY) === '1'; } catch (_) { /* ignore */ }
function dismissScopeNote() { scopeNoteDismissed.value = true; try { localStorage.setItem(SCOPE_NOTE_KEY, '1'); } catch (_) { /* ignore */ } }

onMounted(() => { document.addEventListener('click', closePops); init(); });
onUnmounted(() => { document.removeEventListener('click', closePops); clearTimeout(searchTimer); });
watch(() => props.inboxId, () => {
  closePops();
  respCache.clear();
  Object.keys(buyerTxns).forEach(k => delete buyerTxns[k]);
  Object.keys(expanded).forEach(k => delete expanded[k]);
  init();
});
</script>

<template>
  <div class="ft" :class="{ 'ft-mobile': isMobile }">
    <div v-if="loading" class="ft-empty">Loading…</div>

    <div v-else-if="!webhookOk" class="ft-gate">
      <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4l16 16M4 20L20 4"/><circle cx="12" cy="12" r="9"/></svg>
      <p class="ft-gate-title">Please connect a webhook for live transactions.</p>
      <p class="ft-gate-sub">Open this chat-account's Edit → copy the Webhook URL → register it in Fangate under Settings → API. Sales then appear here in real time.</p>
    </div>

    <template v-else>
      <div class="ft-head">
        <div class="ft-seg">
          <button :class="{ active: view === 'tx' }" @click="switchView('tx')">Transactions &amp; Earnings</button>
          <button :class="{ active: view === 'buy' }" @click="switchView('buy')">Buyers</button>
        </div>
        <div class="ft-hright">
          <div class="ft-search">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></svg>
            <input v-model="search" type="text" placeholder="Search email or product ID…" />
          </div>
          <div class="ft-period">
            <button class="ft-pbtn" @click.stop="togglePeriod">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
              <span>{{ periodLabel }}</span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>
            </button>
            <div v-if="menuOpen" class="ft-pop ft-menu" @click.stop>
              <div v-for="p in PERIODS" :key="p[0]" class="ft-mi" :class="{ cur: period === p[0] }" @click="selectPeriod(p[0])">{{ p[1] }}</div>
            </div>
            <div v-if="calOpen" class="ft-pop ft-cal" @click.stop>
              <div class="ft-calhead">
                <button class="ft-navb" aria-label="Previous month" @click="calView = new Date(calView.getFullYear(), calView.getMonth() - 1, 1)"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg></button>
                <span class="ft-calm">{{ calMonth }}</span>
                <button class="ft-navb" aria-label="Next month" @click="calView = new Date(calView.getFullYear(), calView.getMonth() + 1, 1)"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg></button>
              </div>
              <div class="ft-dow"><span>Mo</span><span>Tu</span><span>We</span><span>Th</span><span>Fr</span><span>Sa</span><span>Su</span></div>
              <div class="ft-grid">
                <div v-for="c in calCells" :key="c.key" class="ft-cd" :class="{ mut: c.muted, rng: c.rng, edge: c.edge }" @click="pickDay(c)">{{ c.d }}</div>
              </div>
              <div class="ft-calfoot"><span>{{ calHint }}</span><button class="ft-apply" :disabled="!selStart || !selEnd" @click="applyCustom">Apply</button></div>
            </div>
          </div>
        </div>
      </div>

      <p v-if="!scopeNoteDismissed" class="ft-scope-note">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
        <span>Tracks only sales that happened <strong>since this account's webhook was registered</strong>. For earlier sales &amp; the account balance, open the <strong>Fangate Vault</strong>.</span>
        <button class="ft-scope-x" type="button" aria-label="Dismiss" @click="dismissScopeNote"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg></button>
      </p>

      <template v-if="view === 'tx'">
        <div class="ft-tiles">
          <div class="ft-tile"><div class="ft-tlbl">Earnings</div><div class="ft-tval earn">{{ fmtCur(tiles.earnings, tiles.cur) }}</div></div>
          <div class="ft-tile"><div class="ft-tlbl">Successful</div><div class="ft-tval succ">{{ tiles.successful }}</div></div>
          <div class="ft-tile"><div class="ft-tlbl">Pending</div><div class="ft-tval pend">{{ tiles.pending }}</div></div>
          <div class="ft-tile"><div class="ft-tlbl">Failed</div><div class="ft-tval fail">{{ tiles.failed }}</div></div>
        </div>
        <div class="ft-controls">
          <div class="ft-tabs">
            <button class="ft-tab" :class="{ active: status === 'all' }" @click="status = 'all'">All</button>
            <button class="ft-tab" :class="{ active: status === 'successful' }" @click="status = 'successful'">Successful</button>
            <button class="ft-tab" :class="{ active: status === 'pending' }" @click="status = 'pending'">Pending</button>
            <button class="ft-tab" :class="{ active: status === 'failed' }" @click="status = 'failed'">Failed</button>
          </div>
          <div class="ft-sortwrap">
            <button class="ft-sortbtn" @click.stop="toggleSort"><span class="mut">Sort:</span><span>{{ sortLabel }}</span><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg></button>
            <div v-if="sortOpen" class="ft-pop ft-menu" @click.stop>
              <div v-for="s in SORTS" :key="s[0]" class="ft-mi" :class="{ cur: sort === s[0] }" @click="selectSort(s[0])">{{ s[1] }}</div>
            </div>
          </div>
        </div>
        <div class="ft-lhead"><span>Date</span><span>Status</span><span>Buyer</span><span>Product</span><span>Product ID</span><span>Content</span><span class="r">Price (est.)</span><span class="r">Seller earning</span><span></span></div>
        <div v-if="!txList.length" class="ft-empty">No transactions in this period.</div>
        <div v-for="t in txList" :key="t.id || t.transaction_id" class="ft-row">
          <span class="date" :data-l="'Date'">{{ fmtDT(t._date) }}</span>
          <span :data-l="'Status'"><span class="ft-pill" :class="normSt(t)"><span class="dot"></span>{{ normSt(t) === 'successful' ? 'Paid' : (normSt(t) === 'pending' ? 'Pending' : 'Failed') }}</span></span>
          <span class="buyer" :data-l="'Buyer'">{{ t.buyer_email }}</span>
          <span class="prod" :data-l="'Product'">{{ pname(t) }}</span>
          <span class="pid" :data-l="'Product ID'">{{ t.product_id }}</span>
          <span :data-l="'Content'"><a class="clink" href="#" @click.prevent="openContent(t)" rel="noopener" title="Opens the content (not the purchase page)">Link <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h6v6M10 14 21 3M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/></svg></a></span>
          <span class="amt r" :data-l="'Price (est.)'">{{ fmtCur(priceEst(t), curOf(t)) }}</span>
          <span class="amt r earn" :data-l="'Seller earning'">{{ normSt(t) === 'successful' ? fmtCur(t.seller_earning, curOf(t)) : '—' }}</span>
          <span class="ft-rowmenu-cell">
            <button v-if="t.buyer_email" class="ft-rowmenu" type="button" title="Assign buyer to a chat" @click.stop="openAssignMenu($event, t)">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.7"/><circle cx="12" cy="12" r="1.7"/><circle cx="12" cy="19" r="1.7"/></svg>
            </button>
          </span>
        </div>
        <div v-if="meta.total > meta.per_page" class="ft-pager">
          <button class="ft-pgb" type="button" :disabled="(meta.page || 1) <= 1" @click="goPage((meta.page || 1) - 1)">‹ Prev</button>
          <span class="ft-pginfo">Page {{ meta.page || 1 }} of {{ totalPages }} · {{ meta.total }} total</span>
          <button class="ft-pgb" type="button" :disabled="!meta.has_more" @click="goPage((meta.page || 1) + 1)">Next ›</button>
        </div>
      </template>

      <template v-else>
        <div class="ft-tiles ft-tiles2">
          <div class="ft-tile"><div class="ft-tlbl">Buyers</div><div class="ft-tval earn">{{ buyerTotals.count }}</div></div>
          <div class="ft-tile"><div class="ft-tlbl">Earnings</div><div class="ft-tval earn">{{ fmtCur(buyerTotals.earn, buyerTotals.cur) }}</div></div>
        </div>
        <div class="ft-bhead"><span>#</span><span>Buyer</span><span>Chat</span><span>Purchases</span><span class="r">Earnings</span></div>
        <div v-if="!buyers.length" class="ft-empty">No buyers in this period.</div>
        <template v-for="(b, i) in buyers" :key="b.email">
          <div class="ft-brow" @click="toggleExpand(b.email)">
            <span class="rank">{{ i + 1 }}</span>
            <span class="buyer"><button class="ft-bchev" :class="{ open: expanded[b.email] }" aria-label="Show purchases"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg></button>{{ b.email }}</span>
            <span class="chat" :title="b.chat_contact ? (b.chat_account + ' · ' + b.chat_contact) : 'Not assigned to a chat'"><template v-if="b.chat_contact"><span class="chat-acc">{{ b.chat_account }}</span> · <span class="chat-name">{{ b.chat_contact }}</span></template><span v-else class="chat-none">—</span></span>
            <span class="cnt">{{ b.count }}</span>
            <span class="amt r earn">{{ fmtCur(b.earn, buyerTotals.cur) }}</span>
          </div>
          <div v-if="expanded[b.email]" class="ft-bdetail">
            <div v-for="t in b.txns" :key="t.id || t.transaction_id" class="ft-bsub">
              <span class="date">{{ fmtDT(t._date) }}</span>
              <span class="prod">{{ pname(t) }}</span>
              <span class="pid">#{{ t.product_id }}</span>
              <span class="amt r earn">{{ fmtCur(t.seller_earning, curOf(t)) }}</span>
            </div>
          </div>
        </template>
        <div v-if="meta.total > meta.per_page" class="ft-pager">
          <button class="ft-pgb" type="button" :disabled="(meta.page || 1) <= 1" @click="goPage((meta.page || 1) - 1)">‹ Prev</button>
          <span class="ft-pginfo">Page {{ meta.page || 1 }} of {{ totalPages }} · {{ meta.total }} buyers</span>
          <button class="ft-pgb" type="button" :disabled="!meta.has_more" @click="goPage((meta.page || 1) + 1)">Next ›</button>
        </div>
      </template>
    </template>

    <!-- ⋮ "Assign to Chat" popover + the shared assign-email-to-chat modal -->
    <div v-if="assignMenu.open" class="ft-am-backdrop" @click="assignMenu.open = false"></div>
    <div v-if="assignMenu.open" class="ft-am-menu" :style="{ left: assignMenu.x + 'px', top: assignMenu.y + 'px' }" @click.stop>
      <button class="ft-am-mi" type="button" @click="openAssignModal">Assign to Chat</button>
    </div>
    <FangateAssignChatModal ref="assignRef" :inbox-id="props.inboxId" @assigned="fetchData(false)" />
  </div>
</template>

<style scoped lang="scss">
.ft { font-family: 'Inter', sans-serif; color: var(--ink-strong); padding: 26px 32px 36px; flex: 1; min-height: 0; overflow-y: auto; }
.ft-empty { padding: 40px; text-align: center; color: var(--ink-muted); font-size: 13.5px; }
.ft-pager { display: flex; align-items: center; justify-content: center; gap: 16px; padding: 18px 0 4px; }
.ft-pgb { border: 1px solid var(--border-soft); background: var(--bg-panel); color: var(--ink-medium); border-radius: 9px; padding: 7px 14px; font: 500 12.5px 'Inter', sans-serif; cursor: pointer; transition: border-color .12s, color .12s; }
.ft-pgb:hover:not(:disabled) { border-color: var(--accent); color: var(--accent-strong); }
.ft-pgb:disabled { opacity: 0.45; cursor: default; }
.ft-pginfo { font: 500 12.5px 'Inter', sans-serif; color: var(--ink-muted); }
.ft-scope-note { display: flex; align-items: center; gap: 8px; margin: 0 0 18px; padding: 9px 13px; border-radius: 9px; background: var(--bg-subtle); border: 1px solid var(--border-soft); font-size: 12px; color: var(--ink-muted); line-height: 1.45; }
.ft-scope-note svg { color: var(--accent-strong); flex-shrink: 0; }
.ft-scope-note strong { color: var(--ink-medium); font-weight: 600; }
.ft-scope-note span { flex: 1; min-width: 0; }
.ft-scope-x { flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; border: none; background: transparent; color: var(--ink-muted); border-radius: 6px; cursor: pointer; transition: background .12s, color .12s; }
.ft-scope-x:hover { background: var(--bg-panel); color: var(--ink-strong); }
.ft-gate { padding: 56px 24px; text-align: center; color: var(--ink-muted); display: flex; flex-direction: column; align-items: center; gap: 12px; }
.ft-gate svg { color: var(--border-medium); }
.ft-gate-title { font-size: 15px; font-weight: 600; color: var(--ink-strong); margin: 0; }
.ft-gate-sub { font-size: 13px; max-width: 460px; margin: 0; line-height: 1.55; }
.ft-head { display: flex; align-items: center; justify-content: space-between; gap: 14px; margin-bottom: 18px; flex-wrap: wrap; }
.ft-seg { display: inline-flex; background: var(--bg-subtle); border-radius: 12px; padding: 4px; gap: 2px; }
.ft-seg button { border: none; background: transparent; border-radius: 9px; padding: 10px 26px; font: 500 15px 'Inter', sans-serif; color: var(--ink-muted); cursor: pointer; transition: background .14s, color .14s; }
.ft-seg button:hover { color: var(--accent-strong); }
.ft-seg button.active { background: linear-gradient(180deg, rgba(20, 160, 148, 0.92), rgba(13, 118, 110, 0.92)); color: #fff; box-shadow: 0 2px 6px rgba(13, 148, 136, 0.30), inset 0 1px 0 rgba(255, 255, 255, 0.20); }
.ft-hright { display: flex; align-items: center; gap: 10px; }
.ft-search { display: inline-flex; align-items: center; gap: 7px; background: var(--bg-panel); border: 1px solid var(--border-soft); border-radius: 11px; padding: 0 12px; height: 40px; overflow: hidden; }
.ft-search:focus-within { border-color: var(--accent); }
.ft-search > svg { color: var(--ink-muted); flex-shrink: 0; }
.ft-search input { border: 0 !important; outline: 0 !important; background: transparent !important; -webkit-appearance: none; appearance: none; box-shadow: none !important; margin: 0 !important; padding: 0 !important; height: auto !important; line-height: normal !important; font: 400 13px 'Inter', sans-serif; color: var(--ink-strong); width: 190px; }
.ft-period { position: relative; }
.ft-pbtn { display: inline-flex; align-items: center; gap: 8px; background: var(--bg-panel); border: 1px solid var(--border-soft); border-radius: 11px; padding: 9px 13px; font: 500 13px 'Inter', sans-serif; color: var(--ink-medium); cursor: pointer; }
.ft-pbtn:hover { border-color: var(--border-medium); }
.ft-pbtn svg:first-child { color: var(--accent-strong); }
.ft-pop { position: absolute; right: 0; top: 46px; z-index: 30; background: var(--bg-panel); border: 1px solid var(--border-medium); border-radius: 14px; box-shadow: var(--shadow-card); padding: 8px; }
.ft-menu { width: 196px; }
.ft-mi { padding: 9px 12px; border-radius: 9px; cursor: pointer; font-size: 14px; color: var(--ink-medium); }
.ft-mi:hover { background: var(--bg-subtle); }
.ft-mi.cur { color: var(--accent-strong); font-weight: 500; }
.ft-cal { width: 300px; }
.ft-calhead { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.ft-calm { font-size: 13px; font-weight: 500; }
.ft-navb { background: transparent; border: 1px solid var(--border-soft); border-radius: 8px; padding: 4px 7px; cursor: pointer; color: var(--ink-medium); display: inline-flex; }
.ft-navb:hover { border-color: var(--border-medium); }
.ft-dow { display: grid; grid-template-columns: repeat(7, 1fr); gap: 3px; font-size: 10.5px; font-weight: 600; color: var(--ink-muted); text-align: center; margin-bottom: 4px; }
.ft-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 3px; }
.ft-cd { aspect-ratio: 1; display: flex; align-items: center; justify-content: center; font-size: 13px; border-radius: 9px; cursor: pointer; }
.ft-cd:hover { background: var(--bg-subtle); }
.ft-cd.rng { background: var(--accent-tint); color: var(--accent-strong); border-radius: 0; }
.ft-cd.edge { background: var(--accent); color: #fff; border-radius: 9px; }
.ft-cd.mut { color: var(--border-medium); cursor: default; }
.ft-calfoot { display: flex; align-items: center; justify-content: space-between; margin-top: 12px; font-size: 12.5px; color: var(--ink-muted); }
.ft-apply { background: var(--accent); border: none; border-radius: 9px; color: #fff; font: 500 13px 'Inter', sans-serif; padding: 6px 16px; cursor: pointer; }
.ft-apply:disabled { background: var(--border-medium); cursor: default; }
.ft-tiles { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; margin-bottom: 18px; }
.ft-tiles2 { grid-template-columns: repeat(2, minmax(0, 1fr)); max-width: 480px; }
.ft-tile { background: var(--bg-subtle); border-radius: 13px; padding: 14px 16px; }
.ft-tlbl { font-size: 11.5px; font-weight: 500; letter-spacing: .05em; text-transform: uppercase; color: var(--ink-muted); margin-bottom: 9px; }
.ft-tval { font-size: 26px; line-height: 1; font-weight: 500; }
.ft-tval.earn { font-family: 'Libre Baskerville', serif; font-weight: 400; color: var(--accent-strong); }
.ft-tval.succ { color: #047857; } .ft-tval.pend { color: #92580b; } .ft-tval.fail { color: #b3261e; }
.ft-controls { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.ft-tabs { display: flex; gap: 8px; }
.ft-tab { padding: 7px 17px; border-radius: 999px; border: none; background: var(--bg-subtle); font: 500 13px 'Inter', sans-serif; color: var(--ink-medium); cursor: pointer; transition: all .14s; box-shadow: 0 1px 2px rgba(31, 42, 42, 0.06); }
.ft-tab:hover { background: var(--border-soft); }
.ft-tab.active { background: linear-gradient(180deg, rgba(20, 160, 148, 0.90), rgba(13, 118, 110, 0.90)); color: #fff; box-shadow: 0 2px 6px rgba(13, 148, 136, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.18); }
.ft-sortwrap { position: relative; }
.ft-sortbtn { display: inline-flex; align-items: center; gap: 7px; background: var(--bg-panel); border: 1px solid var(--border-soft); border-radius: 10px; padding: 7px 12px; font: 500 12.5px 'Inter', sans-serif; color: var(--ink-medium); cursor: pointer; }
.ft-sortbtn .mut { color: var(--ink-muted); font-weight: 400; }
.ft-lhead, .ft-row { display: grid; grid-template-columns: 118px 92px 1.3fr 1.1fr 74px 62px 92px 104px 28px; align-items: center; gap: 10px; }
.ft-bhead, .ft-brow { display: grid; grid-template-columns: 38px 1.5fr 1.4fr 100px 120px; align-items: center; gap: 12px; }
.ft-lhead, .ft-bhead { padding: 12px 8px 9px; font-size: 10.5px; font-weight: 600; letter-spacing: .06em; text-transform: uppercase; color: var(--ink-muted); }
.ft-lhead .r, .ft-bhead .r { text-align: right; }
.ft-row, .ft-brow { padding: 13px 8px; border-top: 1px solid var(--border-soft); font-size: 13.5px; }
.ft-row .date { color: var(--ink-muted); font-size: 12px; }
.buyer { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--ink-medium); }
.prod { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--ink-medium); }
.pid { color: var(--ink-muted); font-size: 12.5px; font-variant-numeric: tabular-nums; }
.amt { font-variant-numeric: tabular-nums; }
.amt.r, .r { text-align: right; }
.amt.earn { font-weight: 600; color: var(--ink-strong); }
.ft-row .amt:not(.earn) { color: var(--ink-muted); }
.rank { color: var(--ink-muted); font-size: 12.5px; font-weight: 600; }
.cnt { color: var(--ink-medium); font-variant-numeric: tabular-nums; }
.ft-pill { display: inline-flex; align-items: center; gap: 6px; padding: 3px 11px; border-radius: 999px; font-size: 12px; font-weight: 500; }
.ft-pill .dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
.ft-pill.successful { background: rgba(16,185,129,.16); color: #047857; }
.ft-pill.pending { background: #fbedd2; color: #92580b; }
.ft-pill.failed { background: #fbe3e3; color: #b3261e; }
.clink { display: inline-flex; align-items: center; gap: 4px; color: var(--accent-strong); font-size: 12.5px; font-weight: 500; text-decoration: none; }
.clink:hover { text-decoration: underline; }
.ft-brow { cursor: pointer; }
.ft-brow:hover { background: var(--bg-subtle); }
.ft-brow .chat { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 12.5px; }
.ft-brow .chat-acc { color: var(--ink-muted); }
.ft-brow .chat-name { color: var(--ink-strong); font-weight: 600; }
.ft-brow .chat-none { color: var(--border-medium); }
.ft-rowmenu-cell { display: flex; justify-content: flex-end; }
.ft-rowmenu { border: none; background: none; padding: 3px; border-radius: 6px; color: var(--ink-muted); cursor: pointer; line-height: 0; display: inline-flex; }
.ft-rowmenu:hover { background: var(--bg-subtle); color: var(--ink-strong); }
.ft-am-backdrop { position: fixed; inset: 0; z-index: 60; }
.ft-am-menu { position: fixed; z-index: 61; background: var(--bg-panel); border: 1px solid var(--border-soft); border-radius: 9px; box-shadow: 0 8px 24px rgba(31, 42, 42, 0.16); padding: 4px; min-width: 150px; }
.ft-am-mi { display: block; width: 100%; text-align: left; border: none; background: none; padding: 7px 11px; border-radius: 6px; font: 500 12.5px 'Inter', sans-serif; color: var(--ink-strong); cursor: pointer; }
.ft-am-mi:hover { background: var(--bg-subtle); }
.ft-bchev { background: transparent; border: none; padding: 0; margin-right: 8px; cursor: pointer; color: var(--ink-muted); display: inline-flex; vertical-align: -2px; transition: transform .14s; }
.ft-bchev.open { transform: rotate(90deg); }
.ft-bdetail { background: var(--bg-subtle); border-top: 1px solid var(--border-soft); padding: 4px 8px 8px 58px; }
.ft-bsub { display: grid; grid-template-columns: 132px 1fr 78px 104px; align-items: center; gap: 11px; padding: 8px 4px; font-size: 12.5px; border-top: 1px solid var(--border-soft); }
.ft-bsub:first-child { border-top: none; }
.ft-bsub .date { color: var(--ink-muted); }

/* Mobile: tiles 2x2, table rows become stacked cards (label via data-l). */
.ft-mobile { padding: 16px 14px 24px; }
.ft-mobile .ft-tiles { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.ft-mobile .ft-head { gap: 10px; }
.ft-mobile .ft-search input { width: 100%; }
.ft-mobile .ft-hright { flex: 1 1 100%; }
.ft-mobile .ft-lhead, .ft-mobile .ft-bhead { display: none; }
.ft-mobile .ft-row { grid-template-columns: 1fr 1fr; gap: 4px 10px; padding: 12px 4px; border-top: 1px solid var(--border-soft); }
.ft-mobile .ft-row > span { display: flex; justify-content: space-between; font-size: 13px; }
.ft-mobile .ft-row > span::before { content: attr(data-l); color: var(--ink-muted); font-size: 11px; margin-right: 8px; }
.ft-mobile .ft-row .buyer, .ft-mobile .ft-row .prod { grid-column: 1 / -1; }
.ft-mobile .ft-rowmenu-cell { display: none; }
.ft-mobile .amt.r, .ft-mobile .r { text-align: left; }
.ft-mobile .ft-brow { grid-template-columns: 28px 1fr auto; }
.ft-mobile .ft-brow .cnt, .ft-mobile .ft-brow .prod, .ft-mobile .ft-brow .chat { display: none; }
.ft-mobile .ft-bsub { grid-template-columns: 1fr auto; }
.ft-mobile .ft-bsub .pid { display: none; }
</style>
